import React from 'react';
import { Check, Minus } from 'lucide-react';

const TONES = {
  free: 'bg-white border border-slate-200 shadow-sm hover:shadow-md',
  pro: 'bg-white border-2 border-[#00247D] shadow-md hover:shadow-xl',
  bundle: 'bg-gradient-to-b from-amber-50 via-white to-white border-2 border-amber-400 ring-4 ring-amber-400/15 shadow-xl hover:shadow-2xl',
};
const BADGE = { pro: 'bg-[#00247D] text-white', bundle: 'bg-amber-500 text-slate-950' };
const HEAD = { fontFamily: 'Barlow Condensed' };

export const usdNum = (n) => (Number.isInteger(Number(n)) ? Number(n) : Number(n).toFixed(2));

export const PlanCardShell = ({ id, tone, badge, title, subtitle, control, price, features, footer, testId, className = '' }) => (
  <div id={id} data-testid={testId}
    className={`relative rounded-2xl p-6 sm:p-8 lg:p-7 xl:p-8 flex flex-col transition-[box-shadow,transform] duration-300 hover:-translate-y-1 ${TONES[tone]} ${className}`}>
    {badge && (
      <span className={`absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-extrabold uppercase tracking-wider px-3.5 py-1 rounded-full shadow-md flex items-center gap-1 ${BADGE[tone]}`}
        data-testid={`${testId}-badge`}>{badge}</span>
    )}
    <h3 className="text-3xl font-black uppercase tracking-wide text-slate-900 leading-none" style={HEAD}>{title}</h3>
    <p className="text-sm text-slate-500 mt-2 md:min-h-[40px]">{subtitle}</p>
    <div className="mt-5 min-h-[46px] flex items-center">{control}</div>
    <div className="mt-5 md:min-h-[150px]" data-testid={`${testId}-price-slot`}>{price}</div>
    <div className="mt-6 pt-6 border-t border-slate-200/80 flex-1">{features}</div>
    <div className="mt-7">{footer}</div>
  </div>
);

// Renders every variant in one grid cell so switching never changes the block's height.
export const Swap = ({ active, items }) => (
  <div className="grid">
    {Object.entries(items).map(([k, node]) => (
      <div key={k} aria-hidden={k !== active}
        className={`[grid-area:1/1] transition-opacity duration-200 ${k === active ? 'opacity-100' : 'opacity-0 invisible pointer-events-none'}`}>
        {node}
      </div>
    ))}
  </div>
);

export const Price = ({ amount, unit, testId, currency = true }) => (
  <div className="flex items-end gap-1.5">
    <span className="text-5xl sm:text-6xl font-black leading-none text-slate-900 tabular-nums" style={HEAD} data-testid={testId}>${usdNum(amount)}</span>
    <span className="pb-1 text-sm text-slate-500">{currency && <b className="text-slate-700">USD</b>} {unit}</span>
  </div>
);

export const PlanToggle = ({ value, options, onChange, testId, tone = 'navy' }) => (
  <div className="w-full grid gap-1 rounded-xl bg-slate-100 border border-slate-200 p-1" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }} role="radiogroup">
    {options.map(([v, label]) => {
      const on = value === v;
      const onCls = tone === 'amber' ? 'bg-amber-500 text-slate-950 shadow-sm' : 'bg-white text-[#00247D] shadow-sm';
      return (
        <button key={v} type="button" role="radio" aria-checked={on} onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
          className={`min-h-[36px] rounded-lg px-1.5 text-xs xl:text-sm font-bold leading-tight transition-[background-color,color,box-shadow] duration-200 ${on ? onCls : 'text-slate-500 hover:text-slate-800'}`}>
          {label}
        </button>
      );
    })}
  </div>
);

export const FeatureList = ({ lead, items, limits = [], testId }) => (
  <ul className="space-y-3 text-sm" data-testid={testId}>
    {lead && <li className="flex items-start gap-2.5 font-bold text-slate-900">{lead}</li>}
    {items.map((f) => (
      <li key={f} className="flex items-start gap-2.5 text-slate-700"><Check className="w-4 h-4 text-[#009E49] shrink-0 mt-0.5" strokeWidth={3} />{f}</li>
    ))}
    {limits.map((f) => (
      <li key={f} className="flex items-start gap-2.5 text-slate-400"><Minus className="w-4 h-4 shrink-0 mt-0.5" />{f}</li>
    ))}
  </ul>
);
